import z from "zod";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { isQueryReady } from "src/api/axiosClient/utils";
import { getRemoteErrorStatus } from "src/api/remoteCollectionState";

export const NETWORK_QUESTIONNAIRE_STATUSES = ["validated", "in_progress", "not_started"] as const;
export type NetworkQuestionnaireStatus = (typeof NETWORK_QUESTIONNAIRE_STATUSES)[number];

const emptyListAsObject = (value: unknown) => (Array.isArray(value) ? {} : value);

const tagsSchema = z.preprocess(emptyListAsObject, z.record(z.string(), z.array(z.string())));

const jsonAttributesSchema = z.preprocess(emptyListAsObject, z.record(z.string(), z.unknown()).nullish());

const NetworkOrganizationSummarySchema = z.object({
    id: z.number(),
    name: z.string(),
    externalId: z.string().nullish(),
    email: z.string().nullish(),
    sectorName: z.string().nullish(),
    country: z.string().nullish(),
    tags: tagsSchema.optional(),
    jsonAttributes: jsonAttributesSchema,
    lastLoginAt: z.string().nullish(),
    parents: z.object({ id: z.number(), name: z.string() }).array(),
});

const joinedParentNames = (parents: { name: string }[]) => parents.map((parent) => parent.name).join(", ") || null;

const NetworkQuestionnaireProgressSchema = z
    .object({
        id: z.string(),
        organization: NetworkOrganizationSummarySchema.extend({ sectorName: z.string() }),
        questionnaire: z.object({ id: z.number(), title: z.string().nullish() }),
        progress: z.object({
            completionRate: z.number(),
            answers: z.number(),
            questions: z.number().nullish(),
            status: z.enum(NETWORK_QUESTIONNAIRE_STATUSES),
            lastChangedAt: z.string().nullish(),
            lastContributorEmail: z.string().nullish(),
        }),
    })
    .transform(({ id, organization, questionnaire, progress }) => ({
        id,
        organizationId: organization.id,
        organizationName: organization.name,
        organizationExternalId: organization.externalId,
        organizationEmail: organization.email,
        parentOrganizationNames: joinedParentNames(organization.parents),
        sectorName: organization.sectorName,
        ssqId: questionnaire.id,
        questionnaireTitle: questionnaire.title,
        ...progress,
        lastLoginAt: organization.lastLoginAt,
        country: organization.country,
        tags: organization.tags,
        jsonAttributes: organization.jsonAttributes,
    }));
export type NetworkQuestionnaireProgress = z.output<typeof NetworkQuestionnaireProgressSchema>;

const NetworkMemberSchema = z
    .object({
        id: z.string(),
        organization: NetworkOrganizationSummarySchema,
        user: z.object({
            id: z.number(),
            firstname: z.string().nullish(),
            lastname: z.string().nullish(),
            email: z.string(),
            lastLoginAt: z.string().nullish(),
            accountCreatedAt: z.string(),
            termsAcceptedAt: z.string().nullish(),
        }),
    })
    .transform(({ id, organization, user }) => ({
        id,
        userId: user.id,
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        organizationId: organization.id,
        organizationName: organization.name,
        organizationExternalId: organization.externalId,
        parentOrganizationNames: joinedParentNames(organization.parents),
        lastLoginAt: user.lastLoginAt,
        accountCreatedAt: user.accountCreatedAt,
        termsAcceptedAt: user.termsAcceptedAt,
    }));
export type NetworkMember = z.output<typeof NetworkMemberSchema>;

const NetworkFilterOptionSchema = z.object({ id: z.number(), label: z.string() });
export type NetworkFilterOption = z.infer<typeof NetworkFilterOptionSchema>;

const NetworkFilterOptionsSchema = z.object({
    organizations: NetworkFilterOptionSchema.array(),
    sectors: NetworkFilterOptionSchema.array(),
    questionnaires: NetworkFilterOptionSchema.array(),
    countries: z.string().array(),
});
export type NetworkFilterOptions = z.infer<typeof NetworkFilterOptionsSchema>;

export const networkQuestionnaireProgressPath = (organizationId: number) =>
    `/api/organizations/${organizationId}/network/questionnaire-progress`;
export const networkMembersPath = (organizationId: number) => `/api/organizations/${organizationId}/network/members`;

type NetworkListOptions = {
    organizationId: number | undefined;
    params: AxiosParams;
    enabled?: boolean;
};

const useNetworkCollection = <Row>(
    queryKey: string,
    path: (organizationId: number) => string,
    schema: z.ZodType<Row>,
    options: NetworkListOptions,
) => {
    const { organizationId, params, enabled = true } = options;
    const isEnabled = enabled && organizationId !== undefined;

    const query = useQuery({
        queryKey: [queryKey, organizationId ?? null, JSON.stringify(params)],
        enabled: isEnabled,
        staleTime: 0,
        cacheTime: 0,
        queryFn: async () => {
            if (organizationId === undefined) {
                throw new Error("Cannot load the network without a selected organization");
            }

            const response = await loggedInApiCall.get(path(organizationId), { params });

            return {
                rows: zodParseWithLog(response.data["hydra:member"], schema.array(), queryKey),
                totalItems: z.number().parse(response.data["hydra:totalItems"]),
            };
        },
    });

    return {
        rows: query.data?.rows ?? [],
        totalItems: query.data?.totalItems ?? 0,
        isReady: isQueryReady(query, isEnabled),
        isRefetching: query.isRefetching,
        errorStatus: query.isError ? getRemoteErrorStatus(query.error) : null,
        refetch: query.refetch,
    };
};

export const useGetNetworkQuestionnaireProgress = (options: NetworkListOptions) =>
    useNetworkCollection(
        "networkQuestionnaireProgress",
        networkQuestionnaireProgressPath,
        NetworkQuestionnaireProgressSchema,
        { ...options, enabled: (options.enabled ?? true) && options.params.campaign !== undefined },
    );

export const useGetNetworkMembers = (options: NetworkListOptions) =>
    useNetworkCollection("networkMembers", networkMembersPath, NetworkMemberSchema, options);

export const useGetNetworkFilterOptions = (organizationId: number | undefined) => {
    const query = useQuery({
        queryKey: ["networkFilterOptions", organizationId ?? null],
        enabled: organizationId !== undefined,
        queryFn: async () => {
            const response = await loggedInApiCall.get(`/api/organizations/${organizationId}/network/filter-options`);

            return zodParseWithLog(response.data, NetworkFilterOptionsSchema, "networkFilterOptions");
        },
    });

    return useMemo(
        () => ({
            ...(query.data ?? { organizations: [], sectors: [], questionnaires: [], countries: [] }),
            errorStatus: query.isError ? getRemoteErrorStatus(query.error) : null,
            refetch: query.refetch,
        }),
        [query.data, query.error, query.isError, query.refetch],
    );
};
